import React, { Ref, useRef, useState } from "react";
import styled from "@emotion/styled";
import Avatar from "@mui/material/Avatar";
import {
  ArrowDropDown as ArrowDropDownIcon,
  Search,
} from "@mui/icons-material";
import {
  IconButton,
  Input,
  Button,
  Menu,
  MenuItem,
  ListItemText,
} from "@mui/material";

import ParkkiTheme from "themes/ParkkiTheme";

const Container = styled.div`
  width: "100%";
  height: "100%";

  .MuiButton-textPrimary {
    padding-left: ${ParkkiTheme.spacings.small};
    padding-right: ${ParkkiTheme.spacings.small};
    color: ${ParkkiTheme.colors.white};
    text-transform: none;
    font-weight: normal;
    color: ${ParkkiTheme.colors.white};
    text-align: center;
    line-height: 20px;
  }
`;

const MenuLabel = styled.div`
  margin-right: ${ParkkiTheme.spacings.tiny};
`;

const StyledAvatar = styled(Avatar)`
  margin-right: ${ParkkiTheme.spacings.small};
`;

const StyledMenuItem = styled(MenuItem)`
  min-width: 350px;
`;

const StyledInput = styled(Input)`
  min-width: 350px;
  padding: 0 10px;
`;

const MenuButton = styled(Button)`
  display: flex;
  flex: 1;
  height: 100%;
  align-items: center;
  justify-content: center;
  padding-left: ${ParkkiTheme.spacings.small};
  padding-right: ${ParkkiTheme.spacings.small};
  text-transform: none;
  font-weight: normal;
  color: ${ParkkiTheme.colors.white};
  text-align: center;
  line-height: 20px;

  &.Mui-disabled {
    color: ${ParkkiTheme.colors.white};
  }
`;

const IconButtonContainer = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  width: 100%;
  height: 100%;
`;

type ContractTypes = {
  name: string;
  id: string;
  disabled: any;
  logo_url: { small: string };
};

type Props = {
  currentContract: ContractTypes;
  contracts: ContractTypes[];
  switchContract: Function;
};

function ContractSwitcher({
  currentContract,
  contracts,
  switchContract,
}: Props): JSX.Element {
  const [anchorElement, setAnchorElement] = useState();
  const [contractFilter, setContractFilter] = useState("");
  const menuItemToFocus: Ref<HTMLLIElement> = useRef(null);

  function handleClick(event: any) {
    setAnchorElement(event.currentTarget);
  }

  function handleClose() {
    setContractFilter("");
    setAnchorElement(undefined);
  }

  function formatContractName(name: string) {
    if (!name) return "";

    return name.length > 30 ? `${name.substring(0, 30)}...` : name;
  }

  return (
    <Container>
      <MenuButton
        disabled={contracts.length === 1}
        aria-controls="customized-menu"
        aria-haspopup="true"
        color="primary"
        aria-owns={anchorElement ? "simple-menu" : undefined}
        onClick={handleClick}
      >
        <IconButtonContainer>
          <StyledAvatar
            src={currentContract?.logo_url?.small}
            alt={currentContract?.name}
          >
            {(currentContract?.name || "").charAt(0)}
          </StyledAvatar>
          <MenuLabel>{currentContract?.name}</MenuLabel>
          {contracts.length > 1 && <ArrowDropDownIcon />}
        </IconButtonContainer>
      </MenuButton>

      {anchorElement && (
        <Menu
          MenuListProps={{ sx: { paddingTop: 0 } }}
          anchorEl={anchorElement}
          open={Boolean(anchorElement)}
          onClose={handleClose}
          keepMounted
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
        >
          {contracts.length > 2 && (
            <StyledInput
              onKeyDown={(event) => {
                event.stopPropagation();
                if (event.code === "ArrowDown") {
                  if (menuItemToFocus.current) {
                    menuItemToFocus.current.focus();
                  }
                }
              }}
              value={contractFilter}
              onChange={(event) => setContractFilter(event.target.value)}
              fullWidth
              endAdornment={
                <IconButton disabled={true}>
                  <Search />
                </IconButton>
              }
            />
          )}
          {contracts
            .filter(
              (contract) =>
                contract.id !== currentContract.id &&
                contract.name
                  .normalize("NFD")
                  .replace(/[\u0300-\u036f]/g, "")
                  .toLowerCase()
                  .includes(
                    contractFilter
                      .normalize("NFD")
                      .replace(/[\u0300-\u036f]/g, "")
                      .toLowerCase()
                  )
            )
            .sort((a, b) => a.disabled - b.disabled)
            .map((contract, index) => (
              <StyledMenuItem
                ref={index === 0 ? menuItemToFocus : undefined}
                key={`${contract.id}-${index}`}
                onClick={() => {
                  switchContract(contract.id);
                  handleClose();
                }}
                selected={contract.id === currentContract.id}
                disabled={contract?.disabled}
              >
                <StyledAvatar
                  src={contract?.logo_url?.small}
                  alt={contract?.name}
                >
                  {(contract?.name || "").charAt(0)}
                </StyledAvatar>
                <ListItemText
                  primary={formatContractName(contract?.name)}
                  style={{ paddingRight: "10px" }}
                />
              </StyledMenuItem>
            ))}
        </Menu>
      )}
    </Container>
  );
}

export default ContractSwitcher;
